import React, {useEffect, useMemo, useState} from 'react';
import './MyStrategiesPage.css';
import tradingService from '../services/tradingService';
import {isCanceledRequest} from '../services/api';
import StrategyCard from '../components/StrategyCard';
import ModalShell from '../components/ModalShell';

export default function MyStrategiesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [strategies, setStrategies] = useState([]);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [createName, setCreateName] = useState('New strategy');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await tradingService.strategies.list({ limit: 200, offset: 0, signal: ac.signal });
        setStrategies(res?.data?.strategies ?? []);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setError(err?.message || 'Failed to load strategies');
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [reloadNonce]);

const openCreate = () => {
    setAccounts([]);
    setAccountNumber('');
    setCreateName('New strategy');
    setCreateDescription('');
    setCreateError('');
    setCreating(false);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setCreateError('');
  };

  const accountOptions = useMemo(() => {
    return (Array.isArray(accounts) ? accounts : [])
      .map((account) => {
        const number = String(account?.number ?? '').trim();
        if (!number) return null;

        const label = String(account?.label ?? '').trim();

        return {
          value: number,
          label: label || 'Account',
        };
      })
      .filter(Boolean);
  }, [accounts]);

  useEffect(() => {
    if (!createOpen) return;

    const ac = new AbortController();

    (async () => {
      try {
        setAccountsLoading(true);
        setCreateError('');

        const res = await tradingService.accounts.list({
          limit: 200,
          offset: 0,
          signal: ac.signal,
        });

        setAccounts(res?.data?.accounts ?? []);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setAccounts([]);
        setCreateError(err?.message || 'Failed to load accounts');
      } finally {
        if (!ac.signal.aborted) {
          setAccountsLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [createOpen]);

  const submitCreate = async (e) => {
    e.preventDefault();

    const nextAccountNumber = (accountNumber ?? '').trim();
    const name = (createName ?? '').trim();
    const description = (createDescription ?? '').trim();

    if (!nextAccountNumber) {
      setCreateError('Account is required');
      return;
    }

    if (!name) {
      setCreateError('Strategy name is required');
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      await tradingService.strategies.upsert({
        payload: {
          account_number: nextAccountNumber,
          name,
          ...(description ? { description } : null),
        },
      });

      setCreateOpen(false);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setCreateError(err?.message || 'Failed to create strategy');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="card strategies-page">
      <div className="strategies-page__header">
        <h1 className="strategies-page__title">Strategies</h1>

        <button
          type="button"
          className="strategies-page__btn strategies-page__btn--primary"
          onClick={openCreate}
          disabled={loading}
        >
          + Add strategy
        </button>
      </div>

      {error ? <p className="strategies-page__error">{error}</p> : null}
      {loading ? <p>Loading…</p> : null}

      {!loading ? (
        <div className="strategies-page__cards">
          {strategies.length === 0 ? (
            <div className="strategies-page__empty-card">
              <div className="strategies-page__empty-title">No strategies yet</div>
              <div className="strategies-page__empty-subtitle">
                Create a strategy for one of your active accounts to start using it.
              </div>
              <button
                type="button"
                className="strategies-page__btn strategies-page__btn--primary strategies-page__empty-action"
                onClick={openCreate}
              >
                + Add strategy
              </button>
            </div>
          ) : (
            strategies.map((s) => (
              <StrategyCard key={s?.id || s?.short_id || 'strategy'} strategy={s} />
            ))
          )}
        </div>
      ) : null}

      <ModalShell
        title="Create strategy"
        open={createOpen}
        onClose={closeCreate}
        footer={
          <>
            <button type="button" onClick={closeCreate} disabled={creating}>
              Cancel
            </button>
            <button
              type="submit"
              form="create-strategy-form"
              disabled={creating || accountsLoading || !accountNumber || !createName.trim()}
            >
              {creating ? 'Creating…' : 'Create strategy'}
            </button>
          </>
        }
      >
        <form id="create-strategy-form" onSubmit={submitCreate} className="strategies-page__modal-form">
          <label>
            <div className="strategies-page__field-title">Account</div>
            <select
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              disabled={creating || accountsLoading}
              required
              className="strategies-page__control"
            >
              <option value="" disabled>
                {accountsLoading ? 'Loading accounts…' : 'Select account…'}
              </option>
              {accountOptions.map((account) => (
                <option key={account.value} value={account.value}>
                  {account.label}
                </option>
              ))}
            </select>
            {!accountsLoading && accountOptions.length === 0 ? (
              <div className="strategies-page__hint">No accounts available.</div>
            ) : null}
          </label>

          <label>
            <div className="strategies-page__field-title">Strategy name</div>
            <input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              disabled={creating}
              autoComplete="off"
              className="strategies-page__control"
              placeholder="e.g. Conservative BTC strategy"
            />
          </label>

          <label>
            <div className="strategies-page__field-title">Description</div>
            <textarea
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              disabled={creating}
              className="strategies-page__control"
              placeholder="Optional notes about this strategy"
              rows={4}
            />
          </label>

          {createError ? <div className="strategies-page__error">{createError}</div> : null}
        </form>
      </ModalShell>
    </div>
  );
}
